import { useState } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, MapPin } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import LeagueCard from "./LeagueCard";
import { useEventsByType } from "@/hooks/useEvents";
import { parseLocalDate } from "@/utils/dateUtils";

interface ExploreLeaguesProps {
  onLeagueClick?: (league: any) => void;
}

const ExploreLeagues = ({ onLeagueClick }: ExploreLeaguesProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSport, setSelectedSport] = useState("todos");
  const [selectedDistance, setSelectedDistance] = useState("todos");
  const [isDistanceFilterOpen, setIsDistanceFilterOpen] = useState(false);

  // Distance options
  const distanceOptions = [
    { value: "todos", label: "Todos", range: "Sin límite" },
    { value: "cercano", label: "Cercano", range: "≤5 km" },
    { value: "medio", label: "Medio", range: "≤10 km" },
    { value: "lejano", label: "Lejano", range: "≤25 km" }
  ];

  // Use real data from database
  const { events: leagueEvents, loading } = useEventsByType('league');
  
  // Map database events to component format
  const leagues = leagueEvents.map(event => ({
    id: event.id, // Use string UUID
    title: event.title,
    sport: event.sport,
    date: event.start_date,
    endDate: event.end_date || '',
    time: new Date(event.start_date).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
    start_time: event.start_time,
    end_time: event.end_time,
    game_days: event.game_days,
    location: event.location,
    participants: 0,
    maxParticipants: event.max_participants || 0,
    registrationFee: Number(event.entry_fee) || 0,
    categories: event.category ? [event.category] : [],
    organizer: event.organizer_company_name || event.organizer_full_name || 'Organizador',
    genderCategories: event.gender_category && event.category 
      ? [`${event.gender_category.charAt(0).toUpperCase() + event.gender_category.slice(1)} ${event.category}`] 
      : event.category ? [event.category] : [],
    subcategories: event.gender_category && event.category && event.subcategory ? 
      { [`${event.gender_category.charAt(0).toUpperCase() + event.gender_category.slice(1)} ${event.category}`]: event.subcategory.split(',').map(s => s.trim()) } : {},
    matchType: (event.match_type as 'singles' | 'dobles' | 'ambos') || 'dobles',
    leagueFormat: (event.league_format === 'escalera' ? 'escalera-piramidal' : event.league_format) as 'round-robin' | 'escalera-piramidal' | 'sumatoria-juegos',
    isLeague: true,
    registrationDeadline: event.registration_deadline || undefined,
    imageUrl: (event as any).image_url || undefined,
    address: event.address || undefined,
    postal_code: event.postal_code || undefined,
    organizer_id: event.organizer_id,
    prizes: [],
    // Additional fields for guaranteed matches calculation
    total_weeks: event.total_weeks || undefined,
    matches_per_week: event.matches_per_week || undefined,
    ladderConfig: (event as any).ladderConfig,
    rules: event.rules,
  }));

  if (loading) {
    return <div className="text-center py-8">Cargando ligas...</div>;
  }

  // Filter leagues based on search, sport and distance
  const filteredLeagues = leagues.filter(league => {
    const matchesSearch = league.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      league.sport.toLowerCase().includes(searchQuery.toLowerCase()) ||
      league.location.toLowerCase().includes(searchQuery.toLowerCase());
    const sportMapping = { 'Pádel': 'Pádel', 'Tenis': 'Tenis', 'Pickleball': 'Pickleball' };
    const sportName = sportMapping[selectedSport as keyof typeof sportMapping] || selectedSport;
    const matchesSport = selectedSport === "todos" || league.sport.toLowerCase() === sportName.toLowerCase();
    console.log(`ExploreLeagues - Filter: ${selectedSport} -> ${sportName}, League sport: ${league.sport}, Matches: ${matchesSport}`);
    
    let matchesDistance = true;
    if (selectedDistance !== "todos") {
      matchesDistance = true; // Placeholder for distance logic
    }
    
    // Filter out leagues with closed registrations
    const calculateDaysUntilDeadline = (deadline: string) => {
      const today = new Date();
      const deadlineDate = new Date(deadline);
      const diffTime = deadlineDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    };
    
    const hasOpenRegistrations = !league.registrationDeadline || calculateDaysUntilDeadline(league.registrationDeadline) >= 0;
    
    // Filter out leagues that have already started (keep visible during start day)
    const hasNotStartedYet = () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const leagueStartDate = parseLocalDate(league.date);
      leagueStartDate.setHours(0, 0, 0, 0);
      return today <= leagueStartDate;
    };
    
    return matchesSearch && matchesSport && matchesDistance && hasOpenRegistrations && hasNotStartedYet();
  });

  // Fixed list of available sports - always show all options
  const availableSports = ["todos", "Pádel", "Tenis", "Pickleball"];

  const handleDistanceSelect = (distance: string) => {
    setSelectedDistance(distance);
    setIsDistanceFilterOpen(false);
  };

  const getDistanceButtonText = () => {
    const selected = distanceOptions.find(option => option.value === selectedDistance);
    return selectedDistance === "todos" ? "Distancia" : selected?.range || "Distancia";
  };

  return (
    <div className="space-y-4">

      {/* Search and Controls */}
      <Card className="bg-white">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Buscar ligas por nombre, deporte o ubicación..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 placeholder:text-xs placeholder:text-gray-400"
                />
              </div>
            </div>
            
            {/* Sport Filter and Distance Filter */}
            <div className="flex gap-2 justify-center">
              {availableSports.map((sport) => (
                <Button
                  key={sport}
                  variant={selectedSport === sport ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedSport(sport)}
                  className="capitalize text-xs md:text-sm h-6 md:h-8 px-2 md:px-4"
                >
                  {sport === "todos" ? "Todos" : sport}
                </Button>
              ))}
              
              {/* Distance Filter */}
              <Sheet open={isDistanceFilterOpen} onOpenChange={setIsDistanceFilterOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant={selectedDistance !== "todos" ? "default" : "outline"}
                    size="sm"
                    className="flex items-center gap-2 text-xs md:text-sm h-6 md:h-8 px-2 md:px-4"
                  >
                    <MapPin className="h-4 w-4" />
                    <span className="hidden sm:inline">{getDistanceButtonText()}</span>
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-80">
                  <SheetHeader>
                    <SheetTitle>Filtrar por distancia</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6 space-y-4">
                    {distanceOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleDistanceSelect(option.value)}
                        className={`w-full p-4 text-left rounded-lg border transition-colors ${
                          selectedDistance === option.value
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium">{option.label}</div>
                            <div className="text-sm text-gray-500">{option.range}</div>
                          </div>
                          <div className={`w-4 h-4 rounded-full border-2 ${
                            selectedDistance === option.value
                              ? "border-primary bg-primary"
                              : "border-gray-300"
                          }`}>
                            {selectedDistance === option.value && (
                              <div className="w-full h-full rounded-full bg-white scale-50"></div>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </CardContent>
      </Card>


      {/* Leagues List - Using LeagueCard component */}
      <div className="grid gap-4">
        {filteredLeagues.length > 0 ? (
          filteredLeagues.map(league => (
            <LeagueCard key={league.id} league={league} onLeagueClick={onLeagueClick} />
          ))
        ) : (
          <Card className="bg-white">
            <CardContent className="p-12 text-center">
              <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No se encontraron ligas</h3>
              <p className="text-gray-600">Intenta ajustar tu búsqueda o filtros</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ExploreLeagues;
